import { unzipSync } from 'fflate'
import { useRef, useState } from 'react'
import { errorMessage } from '../../lib/api.js'
import { useImportDraft } from '../../lib/queries/systems.js'
import { Dialog } from '../ui/Dialog.jsx'
import { useToast } from '../ui/Toast.jsx'
import l from './library.module.css'

// Files kept from inside a folder or .zip (references, assets, scripts).
const TEXT_EXT = /\.(md|markdown|json|txt|py|ya?ml|csv)$/i
// Loose files picked on their own: a .zip or Markdown.
const LOOSE_EXT = /\.(zip|md|markdown)$/i
const MAX_TOTAL = 12 * 1024 * 1024

/** Strip a shared top folder ("p21-cloud-system/SKILL.md" → "SKILL.md"). */
function stripCommonRoot(files) {
  const roots = new Set(files.map((f) => f.path.split('/')[0]))
  if (roots.size !== 1 || files.some((f) => !f.path.includes('/'))) return files
  const root = [...roots][0]
  return files.map((f) => ({ ...f, path: f.path.slice(root.length + 1) }))
}

async function readSelection(fileList) {
  const decoder = new TextDecoder('utf-8', { fatal: false })
  const out = []
  for (const file of fileList) {
    if (/\.zip$/i.test(file.name)) {
      const entries = unzipSync(new Uint8Array(await file.arrayBuffer()))
      for (const [name, bytes] of Object.entries(entries)) {
        if (name.endsWith('/') || name.startsWith('__MACOSX/') || name.split('/').some((p) => p.startsWith('.'))) continue
        if (!TEXT_EXT.test(name)) continue
        out.push({ path: name, content: decoder.decode(bytes) })
      }
    } else {
      // Files from a picked or dropped folder carry their relative path.
      const path = file.relativePath || file.webkitRelativePath || file.name
      const inFolder = path.includes('/')
      if (inFolder ? !TEXT_EXT.test(path) || path.split('/').some((p) => p.startsWith('.')) : !LOOSE_EXT.test(path)) continue
      out.push({ path, content: await file.text() })
    }
  }
  const files = stripCommonRoot(out).filter((f) => f.path && f.path !== 'kb-manifest.json')
  // A kb-manifest.json can carry curated descriptions.
  const manifestEntry = out.find((f) => f.path.endsWith('kb-manifest.json'))
  if (manifestEntry) {
    try {
      const manifest = JSON.parse(manifestEntry.content)
      for (const f of files) {
        const summary = manifest.files?.[f.path]?.summary
        if (summary) f.summary = summary
      }
    } catch {
      /* ignore */
    }
  }
  return files
}

function readEntries(reader) {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject))
}

/** Walk a dropped folder; readEntries returns batches until it returns none. */
async function walkEntry(entry, prefix, out) {
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject))
    file.relativePath = prefix + file.name
    out.push(file)
    return
  }
  const reader = entry.createReader()
  for (let batch = await readEntries(reader); batch.length; batch = await readEntries(reader)) {
    for (const child of batch) await walkEntry(child, `${prefix}${entry.name}/`, out)
  }
}

async function droppedFiles(dataTransfer) {
  const entries = [...dataTransfer.items].map((item) => item.webkitGetAsEntry?.()).filter(Boolean)
  if (!entries.length) return [...dataTransfer.files]
  const out = []
  for (const entry of entries) await walkEntry(entry, '', out)
  return out
}

export function UploadDialog({ system, open, onClose }) {
  const importDraft = useImportDraft(system.slug)
  const toast = useToast()
  const [files, setFiles] = useState([])
  const [replace, setReplace] = useState(true)
  const [error, setError] = useState(null)
  const [over, setOver] = useState(false)
  const filesInput = useRef(null)
  const folderInput = useRef(null)

  const total = files.reduce((sum, f) => sum + f.content.length, 0)
  const hasPrimary = files.some((f) => f.path === 'SKILL.md')

  const accept = async (list) => {
    setError(null)
    try {
      const parsed = await readSelection(list)
      if (!parsed.length) setError('No skill files found. Upload the skill folder, a .zip of it, or .md files.')
      setFiles(parsed)
    } catch (e) {
      setError(`Couldn't read that upload: ${e.message}`)
    }
  }

  const close = () => {
    setFiles([])
    setError(null)
    onClose()
  }

  const submit = () => {
    if (total > MAX_TOTAL) {
      setError('Uploads are limited to 12 MB.')
      return
    }
    importDraft.mutate(
      { files, replace },
      {
        onSuccess: (result) => {
          toast.success(`Imported ${result.files.length} files into the ${system.name} draft`)
          close()
        },
        onError: (e) => setError(errorMessage(e)),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title={`Upload ${system.name} skill`}
      description="Files go into the draft. Answers keep using the published version until you publish."
      width={560}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={close}>
            Cancel
          </button>
          <button type="button" className="btn btn-dark" disabled={!files.length || importDraft.isPending} onClick={submit}>
            {importDraft.isPending ? 'Importing…' : `Import ${files.length || ''} files`}
          </button>
        </>
      }
    >
      <div
        className={`${l.drop} ${over ? l.dropActive : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={async (e) => {
          e.preventDefault()
          setOver(false)
          try {
            accept(await droppedFiles(e.dataTransfer))
          } catch (err) {
            setError(`Couldn't read that upload: ${err.message}`)
          }
        }}
      >
        <strong>Drop the skill folder, a .zip of it, or .md files</strong>
        SKILL.md with its references and assets
        <input
          ref={filesInput}
          type="file"
          multiple
          accept=".zip,.md,.markdown"
          className="sr-only"
          onChange={(e) => {
            accept([...e.target.files])
            e.target.value = ''
          }}
        />
        <input
          ref={folderInput}
          type="file"
          webkitdirectory=""
          directory=""
          className="sr-only"
          onChange={(e) => {
            accept([...e.target.files])
            e.target.value = ''
          }}
        />
        <div className={l.dropButtons}>
          <button type="button" className="btn btn-ghost" onClick={() => folderInput.current?.click()}>
            Choose folder
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => filesInput.current?.click()}>
            Choose .zip or .md files
          </button>
        </div>
      </div>
      {files.length ? (
        <>
          <ul className={l.uploadList}>
            {files.map((f) => (
              <li key={f.path}>
                <span className={l.uploadPath}>{f.path}</span>
                <span>{(f.content.length / 1024).toFixed(1)} KB</span>
              </li>
            ))}
          </ul>
          {!hasPrimary ? <span className="field-error">No SKILL.md at the top level — the skill needs a primary file.</span> : null}
          <label className={l.check}>
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
            Replace every file in the draft (untick to add or update only these files)
          </label>
        </>
      ) : null}
      {error ? <span className="field-error">{error}</span> : null}
    </Dialog>
  )
}
